import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import { BootstrapButton } from "../../../Components/Button/BootstrapButton";
import Cookies from "js-cookie";
import EditIcon from "@mui/icons-material/Edit";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 510,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
  overflowY: "auto",
};

const tableCellStyle = {
  whiteSpace: "nowrap",
  backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
  border: "1px solid hsl(186, 89.90%, 27.30%)",
  color: "#FFF",
  fontWeight: "bold",
  position: "sticky",
  top: 0,
  zIndex: 1000,
  minWidth: 120,
  textAlign: "center",
};

const cellStyle = {
  border: 1,
  borderColor: "grey.300",
  paddingTop: 0,
  paddingBottom: 0,
};

const ModalEditRow = ({ open, onClose, row, onSubmit }) => {
  const [value, setValue] = useState(
    row?.question?.type?.startsWith("MUL")
      ? row?.value?.split(",") || []
      : row?.value || ""
  );
  const hasWarnedRef = useRef(false);

  useEffect(() => {
    const nextValue = row?.question?.type?.startsWith("MUL")
      ? row?.value?.split(",") || []
      : row?.value || "";
    setValue(nextValue);
    hasWarnedRef.current = false;
  }, [row]);

  useEffect(() => {
    if (!open) hasWarnedRef.current = false;
  }, [open]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (!open || hasWarnedRef.current || !row?.question) return;

      const type = row?.question?.type || "";
      const options = row?.question?.answer_items?.map((i) => String(i.code)) || [];

      const warn = (invalidValues) => {
        const msg = Array.isArray(invalidValues)
          ? `Nilai yang dipilih saat ini (${invalidValues.join(", ")}) tidak tersedia dalam daftar pilihan.`
          : `Nilai yang dipilih saat ini (${invalidValues}) tidak tersedia dalam daftar pilihan.`;
        alert(msg);
        hasWarnedRef.current = true;
      };

      if (type.startsWith("SIN")) {
        const current = value == null ? "" : String(value);
        if (current && !options.includes(current)) {
          setValue("");
          warn(current);
        }
      } else if (type.startsWith("MUL")) {
        const currentArr = Array.isArray(value)
          ? value.map(String)
          : (value ? String(value).split(",") : []);

        const filtered = currentArr.filter((v) => options.includes(v));
        const invalid = currentArr.filter((v) => !options.includes(v));

        if (invalid.length > 0) {
          setValue(filtered);
          setTimeout(() => {
            warn(invalid);
          }, 200);
        }
      }
    }, 10);
    return () => clearTimeout(handler);
  }, [open, row, value]);

  const handleCheckboxChange = (code) => {
    setValue((prev) =>
      prev.includes(code) ? prev.filter((v) => v !== code) : [...prev, code]
    );
  };

  const handleSubmit = () => {
    const resultValue = Array.isArray(value)
      ? value.filter((v) => v !== "").join(",")
      : value;
    onSubmit(row.fact, resultValue);
    onClose();
  };

  const renderInput = () => {
    const type = row?.question?.type;

    if (
      [
        "RSPRICE",
        "RPRICE",
        "RSTOCK",
        "NUM_0",
        "NUM_RSALES",
        "NUM_RSTOCK",
      ].includes(type)
    ) {
      return (
        <TextField
          label="Value"
          type="number"
          fullWidth
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    }

    if (["TXT_0", "TXT_4"].includes(type)) {
      return (
        <TextField
          label="Value"
          fullWidth
          multiline={type === "TXT_4"}
          rows={type === "TXT_4" ? 4 : 1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      );
    }

    if (type.startsWith("SIN")) {
      return (
        <FormControl component="fieldset" sx={{width: "100%"}}>
          <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
            <FormLabel component="legend">Pilih Salah Satu</FormLabel>

            {value !== "" && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                sx={{ position: "absolute", right: 0 }}
                onClick={() =>
                  row?.question?.type?.startsWith("MUL") ? setValue([]) : setValue("")
                }
              >
                Reset
              </Button>
            )}
          </Box>
          <RadioGroup value={value} onChange={(e) => setValue(e.target.value)}>
            {row?.question?.answer_items?.map((item) => (
              <FormControlLabel
                key={item.code}
                value={item.code}
                control={<Radio />}
                label={item.answer}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }

    if (type.startsWith("MUL")) {
      return (
        <FormControl component="fieldset" sx={{width: "100%"}}>
          <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
            <FormLabel component="legend">Pilih Beberapa</FormLabel>

            {((Array.isArray(value) && value.length > 0) || (!Array.isArray(value) && value)) && (
              <Button
                variant="outlined"
                size="small"
                sx={{
                  position: "absolute",
                  right: 0,
                  borderColor: "white",
                  color: "white",
                  transition: "border-color 0.3s ease, color 0.3s ease",
                  animation: "changeBtnColor 0s 0.2s forwards",
                  "@keyframes changeBtnColor": {
                    to: {
                      borderColor: "#D32F2F",
                      color: "#D32F2F"
                    }
                  }
                }}
                onClick={() =>
                  row?.question?.type?.startsWith("MUL") ? setValue([]) : setValue("")
                }
              >
                Reset
              </Button>
            )}
          </Box>
          {row?.question?.answer_items?.map((item) => (
            <FormControlLabel
              key={item.code}
              control={
                <Checkbox
                  checked={value.includes(item.code)}
                  onChange={() => handleCheckboxChange(item.code)}
                />
              }
              label={item.answer}
            />
          ))}
        </FormControl>
      );
    }

    return <p>Tipe data tidak dikenali</p>;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Tooltip title="Tutup">
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" mb={2}>
          Edit Data
        </Typography>

        <TextField
          label="Fact"
          fullWidth
          value={row.question.description}
          disabled
          sx={{ mb: 2 }}
        />

        {renderInput()}

        <Button
          fullWidth
          sx={{
            backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
            border: "1px solid hsl(186, 89.90%, 27.30%)",
            borderRadius: "4px",
            color: "#fff",
            fontSize: "14px",
            textTransform: "none",
            height: "fit-content",
            "&:hover": {
              backgroundImage: "linear-gradient(#0991a2, #057d8c)",
            },
            mt: 3,
          }}
          onClick={handleSubmit}
          variant="contained"
        >
          Submit
        </Button>
      </Box>
    </Modal>
  );
};

export const ModalViewEditTrx = ({
  surveyId,
  storeCode,
  selectedProductCode,
  open,
  onClose,
}) => {
  const token = Cookies.get("access_token") || "";

  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState({});
  const [viewTrxLog, setViewTrxLog] = useState([]);

  const [modalOpenLogCorrection, setModalOpenLogCorrection] = useState(false);
  const [editKey, setEditKey] = useState("");

  const [viewTrxEdit, setViewTrxEdit] = useState({
    data: {
      trx_qc: [],
      trx_surveyor: [],
    },
    data_info: {},
    question_type: {},
    hint: {},
  });

  const trxData =
    viewTrxEdit.data.trx_surveyor.length > 0
      ? viewTrxEdit.data.trx_surveyor
      : viewTrxEdit.data.trx_qc.length > 0
      ? viewTrxEdit.data.trx_qc
      : [];

  const surveyorData = viewTrxEdit.data.trx_surveyor || [];
  const qcData = viewTrxEdit.data.trx_qc || [];

  const mergedData =
    surveyorData.length || qcData.length
      ? [
          ...new Set([
            ...surveyorData.map((d) => d.fact),
            ...qcData.map((d) => d.fact),
          ]),
        ].map((factKey) => {
          const surveyorRow = surveyorData.find((d) => d.fact === factKey);
          const qcRow = qcData.find((d) => d.fact === factKey);
          return {
            fact: factKey,
            surveyor: surveyorRow?.value || "",
            qc: qcRow?.value || "",
            question: surveyorRow?.question || qcRow?.question || {},
            isEditable:
              surveyorRow?.question?.is_edit &&
              !surveyorRow?.question?.is_calculate,
            fullSurveyorRow: surveyorRow,
            fullQcRow: qcRow,
          };
        })
      : [];

  const fetchViewTrxEdit = async () => {
    try {
      const urlViewTrx = `${process.env.REACT_APP_API_URL}/api/v1/trx-product/rawdata`;

      const requestBody = {
        survey_id: surveyId,
        store_code: storeCode,
        product_code: selectedProductCode,
      };

      const response = await fetch(urlViewTrx, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 200) {
        const data = await response.json();

        setViewTrxEdit({
          data: data.data,
          data_info: data.data_info,
          question_type: data.question_type,
          hint: data.hint,
        });
      } else {
        setViewTrxEdit({
          data: {
            trx_qc: [],
            trx_surveyor: [],
          },
          data_info: {},
          question_type: {},
          hint: {},
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchViewTrxLog = async () => {
    try {
      const urlViewTrx = `${process.env.REACT_APP_API_URL}/api/v1/trx-product/rawdata/log/correction`;

      const requestBody = {
        survey_id: surveyId,
        store_code: storeCode,
        product_code: selectedProductCode,
      };

      const response = await fetch(urlViewTrx, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 200) {
        const { data } = await response.json();
        setViewTrxLog(data);
      } else {
        setViewTrxLog([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const openModalLogCorrection = () => {
    setModalOpenLogCorrection(true);
    // setSelectedModalStoreCode(
    //   viewTrxInfoForLog.find((item) => item.label === "Kode Toko").value
    // );
    // setSelectedProductCode(
    //   viewTrxInfoForLog.find((item) => item.label === "Kode Produk").value
    // );
  };

  const handleEditClick = (row) => {
    setSelectedRow(row);
  };

  const handleEditSubmit = async (fact, value) => {
    try {
      const editUrl = `${process.env.REACT_APP_API_URL}/api/v1/trx-product/rawdata`;

      let roleCode = null;

      if (viewTrxEdit.data.trx_surveyor.length > 0) {
        roleCode = "13";
      } else if (viewTrxEdit.data.trx_qc.length > 0) {
        roleCode = "14";
      }

      const requestBody = {
        survey_id: surveyId,
        store_code: storeCode,
        product_code: selectedProductCode,
        fact: fact,
        value: value,
        role_code: roleCode,
      };

      const response = await fetch(editUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 200) {
        alert("Data berhasil diubah!");
        await fetchViewTrxEdit();
      } else if (
        response.status === 422 ||
        response.status === 400 ||
        response.status === 404
      ) {
        const { detail } = await response.json();
        alert(`${detail}`);
      } else if (response.status === 500) {
        alert("Internal Server Error");
      } else {
        alert("Gagal mengubah data!");
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (open && surveyId && storeCode && selectedProductCode) {
      const handler = setTimeout(() => {
        fetchViewTrxEdit();
      }, 300);
      return () => clearTimeout(handler);
    }
  }, [open, surveyId, storeCode, selectedProductCode]);

  useEffect(() => {
    if (
      modalOpenLogCorrection &&
      surveyId &&
      storeCode &&
      selectedProductCode
    ) {
      const handler = setTimeout(() => {
        fetchViewTrxLog();
      }, 300);
      return () => clearTimeout(handler);
    }
  }, [modalOpenLogCorrection, surveyId, storeCode, selectedProductCode]);

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          onClose();
          setViewTrxEdit({
            data: {
              trx_qc: [],
              trx_surveyor: [],
            },
            data_info: {},
            question_type: {},
            hint: {},
          });
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            ...style,
            width: 1300,
            margin: "0 auto",
            maxHeight: "700px",
            overflow: "auto",
            overflowX: "hidden",
          }}
        >
          <Tooltip title="Close View Edit">
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => {
                onClose();
                setViewTrxEdit({
                  data: {
                    trx_qc: [],
                    trx_surveyor: [],
                  },
                  data_info: {},
                  question_type: {},
                  hint: {},
                });
              }}
              sx={{ position: "absolute", top: 20, right: 30 }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <h2 style={{ marginLeft: "20px" }}>View Edit</h2>
          {mergedData.length > 0 ? (
            <div style={{ height: 600, width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <p style={{ marginLeft: "20px" }}>
                    Nama Toko: {viewTrxEdit.data_info.store_name || "-"}
                  </p>
                  <p style={{ marginLeft: "20px" }}>
                    Code: {viewTrxEdit.data_info.product_code || "-"}
                  </p>
                  <p style={{ marginLeft: "20px" }}>
                    Product: {viewTrxEdit.data_info.product_name || "-"}
                  </p>
                </div>
                <Button
                  sx={{
                    backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                    border: "1px solid hsl(186, 89.90%, 27.30%)",
                    borderRadius: "4px",
                    color: "#fff",
                    fontSize: "14px",
                    textTransform: "none",
                    height: "fit-content",
                    "&:hover": {
                      backgroundImage: "linear-gradient(#0991a2, #057d8c)",
                    },
                  }}
                  onClick={openModalLogCorrection}
                >
                  Log Correction
                </Button>
              </div>

              <TableContainer sx={{ maxHeight: 600, mt: 2, mx: 2, mb: 4 }}>
                <Table
                  stickyHeader
                  sx={{
                    border: 1,
                    borderColor: "grey.300",
                    borderCollapse: "collapse",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell style={tableCellStyle}>FACT</TableCell>
                      <TableCell style={tableCellStyle}>Surveyor</TableCell>
                      <TableCell style={tableCellStyle}>QC</TableCell>
                      <TableCell style={tableCellStyle}>ACTIONS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mergedData.map((row) => (
                      <TableRow key={row.fact}>
                        <TableCell sx={cellStyle}>{row.question.description}</TableCell>
                        <TableCell sx={cellStyle}>
                          {row.surveyor || "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>{row.qc || "-"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {row.isEditable && (
                            <Tooltip
                              title="Edit Data"
                              onClick={() =>
                                handleEditClick(
                                  row.fullSurveyorRow
                                    ? row.fullSurveyorRow
                                    : row.fullQcRow || ""
                                )
                              }
                            >
                              <IconButton>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          ) : (
            <div>
              <p style={{ marginLeft: "20px" }}>Data Kosong</p>
            </div>
          )}
        </Box>
      </Modal>

      <Modal
        open={modalOpenLogCorrection}
        onClose={() => setModalOpenLogCorrection(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            ...style,
            width: 1300,
            margin: "0 auto",
            maxHeight: "700px",
            overflow: "auto",
            overflowX: "hidden",
          }}
        >
          <Tooltip title="Close Log Correction">
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setModalOpenLogCorrection(false)}
              sx={{ position: "absolute", top: 20, right: 30 }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <h2 style={{ marginLeft: "20px" }}>Log Correction</h2>
          {viewTrxLog.length > 0 ? (
            <div style={{ height: 600, width: "100%" }}>
              <TableContainer sx={{ maxHeight: 600, mt: 2, mx: 2, mb: 4 }}>
                <Table
                  stickyHeader
                  sx={{
                    border: 1,
                    borderColor: "grey.300",
                    borderCollapse: "collapse",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {viewTrxLog.length > 0 &&
                        Object.keys(viewTrxLog[0])
                          .filter((key) => key !== "id")
                          .map((key) => (
                            <TableCell key={key} style={tableCellStyle}>
                              {key.toUpperCase()}
                            </TableCell>
                          ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {viewTrxLog.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          height: "20px",
                          padding: 0,
                        }}
                      >
                        {Object.keys(row)
                          .filter((key) => key !== "id")
                          .map((key) => (
                            <TableCell
                              key={key}
                              align={key === "question" ? "center" : "left"}
                              sx={{
                                border: 1,
                                paddingTop: 0,
                                paddingBottom: 0,
                                borderColor: "grey.300",
                              }}
                            >
                              {key === "question" &&
                              row[key]?.is_edit &&
                              row[key]?.is_calculate === false ? (
                                <Tooltip
                                  title="Edit Data"
                                  onClick={() => handleEditClick(row)}
                                >
                                  <IconButton>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                row[key] || "-"
                              )}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          ) : (
            <div>
              <p style={{ marginLeft: "20px" }}>Data Kosong</p>
            </div>
          )}
        </Box>
      </Modal>

      {selectedRow && (
        <ModalEditRow
          open={Boolean(selectedRow)}
          onClose={() => setSelectedRow(null)}
          row={selectedRow}
          onSubmit={handleEditSubmit}
        />
      )}
    </>
  );
};
